import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DangerButton from "../common/DangerButton";
import ErrorModal from "../modals/ErrorModal";
import LoadingBar from "../LoadingBar";
import PdpaConsent from "../modals/pdpaConsent";
import { useLiffProfile } from "../../context/LiffProfileContext";
import OtpForm from "../otp/OtpForm";

function LoginForm() {
  const { t, i18n } = useTranslation();
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("idCard");
  const [error, setError] = useState("");
  const { authToken, userId } = useLiffProfile();
  const [showPdpaModal, setShowPdpaModal] = useState(false);
  const [pdpaPayload, setPdpaPayload] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idCardError, setIdCardError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [passportError, setPassportError] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otpPayload, setOtpPayload] = useState(null);
  const [otpRef, setOtpRef] = useState("");

  const [passportMobileError, setPassportMobileError] = useState("");
  const [formData, setFormData] = useState({
    idCard: "",
    mobilePhone: "",
    PassportId: "",
    mobile_number: "",
  });

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async () => {
    let payload = {};
    let hasError = false;

    if (activeTab === "idCard") {
      if (!formData.idCard || formData.idCard.length < 13) {
        setIdCardError(t("form_item_invalid_citizen_id_format"));
        hasError = true;
      } else {
        setIdCardError("");
      }

      if (!formData.mobilePhone) {
        setMobileError(t("form_item_invalid_mobile_number_format"));
        hasError = true;
      } else {
        setMobileError("");
      }

      payload = {
        idCard: formData.idCard,
        mobilePhone: formData.mobilePhone,
      };
    } else if (activeTab === "passport") {
      if (!formData.PassportId) {
        setPassportError(t("form_item_invalid_passport_id_format"));
        hasError = true;
      } else {
        setPassportError("");
      }

      if (!formData.Phone_passport) {
        setPassportMobileError(t("form_item_invalid_mobile_number_format"));
        hasError = true;
      } else {
        setPassportMobileError("");
      }

      payload = {
        idCard: formData.PassportId,
        mobilePhone: formData.Phone_passport,
      };
    }

    if (hasError) return;

    // console.log("Authorization:", process.env.REACT_APP_CLIENT_AUTH);
    // console.log("Login payload:", payload);
    setLoading(true);

    try {
      const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
      const apiUrl =
        process.env.REACT_APP_API_URL + "/api/users/verify?replace=false";
      const res = await axios.put(apiUrl, payload, {
        headers: {
          Authorization: "Bearer " + authToken,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
        },
      });
      // console.log("Login payload (passport ):", payload);

      const { statusCode, message, data } = res.data;
      if (statusCode === 200) {
        //if (activeTab === "passport") {
        setOtpPayload({
          mobile: payload.mobilePhone,
          id: payload.idCard,
          // consentStatus: 1, // หรือ 1/0 ถ้ามีค่า default
        });
        setShowOtp(true);
        setOtpRef(data.otpRef);
        return;
        //}
      } else if (statusCode === 400) {
        if (message === "PDPA_CONSENT_REQUIRED") {
          setPdpaPayload({
            mode: activeTab,
            mobile: payload.mobilePhone,
            id: payload.idCard,
            payload: payload,
          });
          setShowPdpaModal(true);
        } else {
          setError(message || t("login_error_unknown"));
          setShowErrorModal(true);
        }
      } else if (statusCode === 404) {
        if (message === "NOT_FOUND_BIGCARD") {
          setError(t("login_error_user_with_passport_not_found"));
        } else {
          setError(message || t("login_error_unknown"));
        }
        setShowErrorModal(true);
      } else if (statusCode === 409) {
        if (
          message === "HAS_ANOTHER_USER_ID" ||
          message === "HAS_ANOTHER_CARD" ||
          message === "HAS_ANOTHER_LINEMID"
        ) {
          setError(t("login_error_used_line_id"));
        } else {
          setError(message || t("login_error_unknown"));
        }
        setShowErrorModal(true);
      } else if (statusCode === 406) {
        setError(t("login_error_incorrect_info"));
        setShowErrorModal(true);
      } else {
        setError(message || t("login_error_unknown"));
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.message || t("login_error_unknown"));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePdpaAccept = () => {
    setShowPdpaModal(false);
    if (pdpaPayload) {
      navigate("/SuccessBindingForm", { state: pdpaPayload });
    }
  };

  const handleOtpSubmit = (finalPayload) => {
    // console.log("✅ OTP confirmed from passport login:", finalPayload);
    navigate("/SuccessBindingForm", { state: finalPayload });
  };

  return (
    <div className="flex justify-center px-4 py-10 sm:px-6 sm:py-8 bg-secondary-background-gray">
      <div className="relative w-full max-w-md p-5 flex flex-col justify-between">
        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-6 sm:gap-4 xs:gap-2">
            <div className="text-3xl  sm:text-2xl font-semibold text-left">
              {t("login_with_big_point")} <br />
            </div>
            <div className="w-full h-13 flex bg-overlay-5 rounded-full font-medium p-1 cursor-pointer text-lg sm:text-base">
              <div
                className={`flex-1 h-full flex justify-center items-center rounded-full p-3 ${
                  activeTab === "idCard" ? "bg-[#93c93d] text-white" : ""
                }`}
                onClick={() => handleTabClick("idCard")}
              >
                {t("with_citizen")}
              </div>
              <div
                className={`flex-1 h-full flex justify-center items-center rounded-full p-3 ${
                  activeTab === "passport" ? "bg-[#93c93d] text-white" : ""
                }`}
                onClick={() => handleTabClick("passport")}
              >
                {t("with_passport_title")}
              </div>
            </div>

            {activeTab === "idCard" && (
              <>
                <div className="flex flex-col pt-5">
                  <label className="block text-base font-medium mb-1 text-secondary-label">
                    <div className="flex items-center">
                      {t("citizen_id")}
                      <div className="text-danger">*</div>
                    </div>
                  </label>

                  {/* <input
                    type="text"
                    name="idCard"
                    maxLength={19}
                    inputMode="numeric"
                    value={formData.idCard.replace(
                      /(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/,
                      "$1 $2 $3 $4 $5"
                    )}
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 13);
                      setFormData((prev) => ({
                        ...prev,
                        idCard: rawValue,
                      }));
                      if (rawValue.length === 13) {
                        setIdCardError(""); // clear error เมื่อกรอกครบ
                      }
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("citizen_id_placeholder")}
                  /> */}
                  <input
                    type="text"
                    name="idCard"
                    maxLength={19}
                    inputMode="numeric"
                    value={formData.idCard.replace(
                      /(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/,
                      "$1 $2 $3 $4 $5"
                    )}
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 13);
                      setFormData((prev) => ({
                        ...prev,
                        idCard: rawValue,
                      }));
                      if (rawValue.length > 0 && rawValue.length < 13) {
                        setIdCardError("กรุณากรอกตัวเลข 13 หลัก");
                      } else {
                        setIdCardError("");
                      }
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("citizen_id_placeholder")}
                  />
                  {idCardError && (
                    <span className="text-red-500 text-sm mt-1 text-left">
                      {idCardError}
                    </span>
                  )}
                </div>
                <div className="flex flex-col pt-5">
                  <label className="block text-base font-medium mb-1 text-secondary-label">
                    <div className="flex items-center">
                      {t("mobile_number")}
                      <div className="text-danger">*</div>
                    </div>
                  </label>

                  {/* <input
                    type="number"
                    name="mobilePhone"
                    maxLength={12} // 10 digits + 2 dashes
                    inputMode="numeric"
                    value={
                      formData.mobilePhone.length >= 10
                        ? formData.mobilePhone.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "$1-$2-$3"
                          )
                        : formData.mobilePhone
                    }
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10); // เก็บเลขล้วน
                      setFormData((prev) => ({
                        ...prev,
                        mobilePhone: rawValue,
                      }));
                      if (rawValue) setMobileError(""); // clear error เมื่อกรอก
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("form_item_invalid_mobile_number_format")}
                  /> */}
                  <input
                    type="text"
                    name="mobilePhone"
                    maxLength={12}
                    inputMode="numeric"
                    value={
                      formData.mobilePhone.length >= 10
                        ? formData.mobilePhone.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "$1-$2-$3"
                          )
                        : formData.mobilePhone
                    }
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData((prev) => ({
                        ...prev,
                        mobilePhone: rawValue,
                      }));
                      if (rawValue.length > 0 && rawValue.length < 10) {
                        setMobileError("กรุณากรอกเบอร์โทรศัพท์ 10 หลัก");
                      } else {
                        setMobileError("");
                      }
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("form_item_invalid_mobile_number_format")}
                  />
                  {mobileError && (
                    <span className="text-red-500 text-sm mt-1 text-left">
                      {mobileError}
                    </span>
                  )}
                </div>
              </>
            )}

            {activeTab === "passport" && (
              <>
                <div className="flex flex-col pt-5">
                  <label className="block text-base font-medium mb-1 text-secondary-label">
                    <div className="flex items-center">
                      {t("with_passport_placeholder")}
                      <div className="text-danger">*</div>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="PassportId"
                    maxLength={9}
                    inputMode="text"
                    value={formData.PassportId}
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .slice(0, 9);
                      setFormData((prev) => ({
                        ...prev,
                        PassportId: rawValue,
                      }));
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("with_passport_placeholder")}
                  />
                  {passportError && (
                    <span className="text-red-500 text-sm mt-1 text-left">
                      {passportError}
                    </span>
                  )}
                  {/* <input
                    type="text"
                    name="PassportId"
                    maxLength={9}
                    inputMode="text"
                    pattern="[A-Za-z0-9]{6,9}"
                    value={formData.PassportId}
                    onChange={handleChange}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(
                        /[^a-zA-Z0-9]/g,
                        ""
                      );
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input text-secondary-text placeholder-secondary-placeholder focus:outline-none focus:border-primary"
                    placeholder={t("passport_id_placeholder")}
                  /> */}
                </div>
                <div className="flex flex-col pt-5">
                  <label className="block text-base font-medium mb-1 text-secondary-label">
                    <div className="flex items-center">
                      {t("mobile_number")}
                      <div className="text-danger">*</div>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="Phone_passport"
                    maxLength={12}
                    inputMode="numeric"
                    value={
                      formData.Phone_passport
                        ? formData.Phone_passport.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "$1-$2-$3"
                          )
                        : formData.Phone_passport
                    }
                    onChange={(e) => {
                      let rawValue = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData((prev) => ({
                        ...prev,
                        Phone_passport: rawValue,
                      }));
                      if (rawValue) setMobileError("");
                    }}
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("form_item_invalid_mobile_number_format")}
                  />

                  {passportMobileError && (
                    <span className="text-red-500 text-sm mt-1 text-left">
                      {passportMobileError}
                    </span>
                  )}

                  {/* {mobileError && (
                    <span className="text-red-500 text-sm mt-1">
                      {mobileError}
                    </span>
                  )} */}

                  {/* <input
                    type="text"
                    name="Phone_passport"
                    maxLength={10}
                    inputMode="numeric"
                    pattern="0\d{9}"
                    value={formData.Phone_passport}
                    onChange={handleChange}
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/\D/g, ""))
                    }
                    className="w-full h-11 border-none rounded-lg px-4 bg-secondary-input"
                    placeholder={t("form_item_invalid_mobile_number_format")}
                  /> */}
                </div>
              </>
            )}

            <div className="h-5 text-sm font-normal text-secondary-text-note mt-2 ml-4 text-left">
              *{t("only_for_thai_mobile_number")}
            </div>

            <div className="flex justify-center items-center mt-6">
              <DangerButton buttonName={t("login")} onClick={handleLogin} />
            </div>
          </div>
        </form>

        <div className="w-full flex justify-center items-center flex-wrap gap-2 sm:mt-8 mt-14">
          <span className="font-medium text-lg">{t("first_time_login")}</span>
          <a
            href="/register"
            className="text-primary-text underline font-medium text-xl"
          >
            {t("register_big_point")}
          </a>
        </div>
      </div>
      <LoadingBar loading={loading} label="Loading..." />
      {showErrorModal && (
        <ErrorModal error={error} onClose={() => setShowErrorModal(false)} />
      )}
      {showPdpaModal && (
        <PdpaConsent
          show={showPdpaModal}
          onAccept={handlePdpaAccept}
          onClose={() => setShowPdpaModal(false)}
          payload={pdpaPayload}
        />
      )}
      {showOtp && otpPayload && (
        <OtpForm
          mobilePhone={otpPayload.mobile}
          idCard={otpPayload.id}
          // consentStatus={otpPayload.consentStatus}
          otpRef={otpRef}
          onSubmit={handleOtpSubmit}
          onClose={() => setShowOtp(false)}
        />
      )}
    </div>
  );
}

export default LoginForm;
